import { defaultThemeV2 } from "../../theme/defaultThemeV2";
import type { QuestionCore } from "../contracts";

export type ConjointRaw = {
  type: "conjoint";
  content: string;
  options: {
    value: string;
    text: string;
    image: string | null;
    description?: string | null;
    disabled: boolean;
    display_text: boolean;
  }[];
  rows: { value: string; text: string; display_text: boolean }[];
  row_options: { value: string; text: string }[][];
  none_option: { value: string; text: string } | null;
  dont_know_option: { value: string; text: string } | null;
  previous_response?: string;
  image?: string | null;
  alt_text?: string | null;
  error_message?: string;
};

export type ConjointDefinition = {
  prompt: string;
  concepts: readonly {
    id: string;
    label: string;
    image?: string;
    description?: string;
    disabled: boolean;
    displayLabel: boolean;
  }[];
  attributes: readonly {
    id: string;
    label: string;
    values: readonly string[];
  }[];
  none?: { value: string; label: string };
  dontKnow?: { value: string; label: string };
  previousResponse?: string;
  image?: string;
  altText?: string;
  error?: string;
};

export type ConjointDraft = { selectedId: string | null };
export type ConjointEvent = { type: "select"; value: string };
export type ConjointResponse = { type: "conjoint"; value: string };

const choice = (definition: ConjointDefinition, value: string | null) => {
  if (value === null) return undefined;
  if (value === definition.none?.value) return definition.none;
  if (value === definition.dontKnow?.value) return definition.dontKnow;
  return definition.concepts.find(
    (concept) => concept.id === value && !concept.disabled,
  );
};

export const conjointCore: QuestionCore<
  ConjointRaw,
  ConjointDefinition,
  ConjointDraft,
  ConjointEvent,
  ConjointResponse
> = {
  decode: (raw) => {
    if (
      !Array.isArray(raw.options) ||
      raw.options.length === 0 ||
      !Array.isArray(raw.rows) ||
      !Array.isArray(raw.row_options) ||
      raw.rows.length !== raw.row_options.length ||
      raw.row_options.some(
        (cells) => !Array.isArray(cells) || cells.length !== raw.options.length,
      )
    ) {
      throw new Error(
        "Conjoint requires concepts and an aligned attribute matrix.",
      );
    }
    const conceptIds = raw.options.map((option) => option.value);
    const attributeIds = raw.rows.map((row) => row.value);
    const fixedIds = [
      raw.none_option?.value,
      raw.dont_know_option?.value,
    ].filter((id): id is string => id !== undefined);
    if (
      [...conceptIds, ...fixedIds].some(
        (id) => typeof id !== "string" || !id,
      ) ||
      new Set([...conceptIds, ...fixedIds]).size !==
        conceptIds.length + fixedIds.length ||
      attributeIds.some((id) => typeof id !== "string" || !id) ||
      new Set(attributeIds).size !== attributeIds.length
    ) {
      throw new Error(
        "Conjoint concept, fixed choice and attribute IDs must be distinct.",
      );
    }
    return {
      prompt: raw.content,
      concepts: raw.options.map((option) => ({
        id: option.value,
        label: option.text,
        disabled: option.disabled,
        displayLabel: option.display_text,
        ...(option.image ? { image: option.image } : {}),
        ...(option.description ? { description: option.description } : {}),
      })),
      attributes: raw.rows.map((row, index) => ({
        id: row.value,
        label: row.text,
        values: raw.row_options[index].map((cell) => cell.text),
      })),
      ...(raw.none_option
        ? {
            none: { value: raw.none_option.value, label: raw.none_option.text },
          }
        : {}),
      ...(raw.dont_know_option
        ? {
            dontKnow: {
              value: raw.dont_know_option.value,
              label: raw.dont_know_option.text,
            },
          }
        : {}),
      ...(raw.previous_response !== undefined
        ? { previousResponse: raw.previous_response }
        : {}),
      ...(raw.image ? { image: raw.image } : {}),
      ...(raw.alt_text ? { altText: raw.alt_text } : {}),
      ...(raw.error_message ? { error: raw.error_message } : {}),
    };
  },
  initialize: (definition) => ({
    selectedId: choice(definition, definition.previousResponse ?? null)
      ? definition.previousResponse!
      : null,
  }),
  update: (definition, draft, event) =>
    choice(definition, event.value) ? { selectedId: event.value } : draft,
  validate: (definition, draft, copy = defaultThemeV2.copy) =>
    choice(definition, draft.selectedId)
      ? { valid: true }
      : { valid: false, error: copy.validation.chooseOne },
  encode: (definition, draft) => {
    const validation = conjointCore.validate(definition, draft);
    if (!validation.valid) throw new Error(validation.error);
    return { type: "conjoint", value: draft.selectedId! };
  },
  projectTranscript: (definition, draft) => {
    const validation = conjointCore.validate(definition, draft);
    if (!validation.valid) throw new Error(validation.error);
    const index = definition.concepts.findIndex(
      (concept) => concept.id === draft.selectedId,
    );
    const answer =
      index < 0
        ? choice(definition, draft.selectedId)!.label
        : [
            definition.concepts[index].displayLabel
              ? definition.concepts[index].label
              : `Option ${index + 1}`,
            ...definition.attributes.map(
              (attribute) => `${attribute.label}: ${attribute.values[index]}`,
            ),
          ].join("\n");
    return { kind: "conjoint", question: definition.prompt, answer };
  },
};
